import { Injectable } from '@angular/core';
import { inject } from '@angular/core';

import { StorageAPIService, StorageListResult } from 'app/providers/storage-api.service';
import { LoggerService } from 'app/services/logger.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { HighlightLocation } from '../nlp-output.service';
import { WidgetDataSource } from '../../models';
import { DocumentAnalysis } from './analysis.service';
import { Annotation } from './annotation.service';
import { urlFor } from './utils';

export interface SearchOptions {
  facets?: number;
  detailed?: string;
  sortField?: string;
  sortOrder?: string;
  'page-token'?: string;
  facetPrefix?: string;
  facetCustomItems?: string;
  facetCustomType?: string;
  timezone?: string;
  facetTypes?: FacetType[];
  facetMetadataName?: string[];
  countOnly?: boolean;
  isSocial?: boolean;
}

export interface JourneyGroupDocsContentChannel {
  id: string;
  label: string;
}

export interface JourneyGroupDocsDerivedMetric {
  field: string;
  scoreType: string;
  docIdToScore: Record<string, number>;
}

export interface JourneyGroupDocsAggregation {
  nlpAggregations: Record<string, unknown[]>;
  derivedMetrics: JourneyGroupDocsDerivedMetric[];
  collectionIdToContentChannel: Record<string, JourneyGroupDocsContentChannel>;
}

export interface JourneyGroupDocsResult extends StorageListResult<Document> {
  resultSetAggregation?: JourneyGroupDocsAggregation;
}

export type FacetType =
  | 'metadata'
  | 'sentiment'
  | 'topics'
  | 'entities'
  | 'phrases'
  | 'themes'
  | 'autoCategories'
  | 'source'
  | 'creator'
  | 'publisher'
  | 'url'
  | 'language'
  | 'type'
  | 'tags'
  | 'dates'
  | 'social'
  | 'sectionMetadata'
  | 'sectionProperties.sectionLabel'
  | 'sectionProperties.sectionType'
  | 'sectionProperties.speakerName'
  | 'sectionProperties.speakerType'
  | 'sectionProperties.segment'
  | 'sectionProperties.aliases';

export interface Document {
  id: string;
  text: string;
  createdAt: Date;
  indexedAt: Date;
  language: string;
  summary: null;
  tags: string[];
  version: number;
  metadata: {
    [field: string]: any;
  };
  collectionName?: string;
  analyses?: DocumentAnalysis[];
  [field: string]: any;
  source?: string;
  url?: string;
  parentId?: string;
  publisher?: string;
  creator?: string;
  type?: string;
  locationId?: string;
  annotations?: Annotation<any>[];
  sectioned?: boolean;
  vectorMetrics?: VectorMetric[];
  scalarMetrics?: ScalarMetric[];
  highlightLocations?: HighlightLocation[];
}

export interface VectorMetric {
  scoreName: string;
  beginValue: number;
  scaleMin: number;
  scaleMax: number;
  totalChangeValue: number;
  baselineCalibrationSectionStart: number;
  baselineCalibrationSectionEnd: number;
  keyMoments: VectorMetricKeyMoment[];
}

export interface VectorMetricKeyMoment {
  keyMomentId: number;
  keyMomentChangeValue: number;
  justification: string;
  sectionStart: number;
  sectionEnd: number;
}

export interface SectionKeyMoment {
  scoreName: string;
  keyMomentId: number;
  sectionChangeValue: number;
}

export interface ScalarMetric {
  scoreName: string;
  value: number;
  scaleMin: number;
  scaleMax: number;
}

export interface FacetList {
  [analyticItem: string]: { [key: string]: number };
}

export interface FacetListResult {
  facets?: FacetList;
}

export interface DocumentPatchResponse {
  added: number;
  errors: number;
  items: { id: string; status: string; version: number }[];
  id: string;
  status: string;
  submitted: number;
  updated: number;
}

export interface DocumentFacetListResult extends StorageListResult<Document>, FacetListResult {}

export interface TagDocumentsResponse {
  updated: number;
}

export interface DocumentSmartSummary {
  id: string;
  results: [{ text: string }];
}

export interface MediaUrlResponse {
  url?: string;
  expiresAt?: Date;
  message?: string;
}

@EnableClassTelemetry()
@Injectable()
export class DocumentService extends StorageAPIService {
  private readonly logger = inject(LoggerService);

  getDocumentsDownloadUrl(
    collectionName: string,
    analysisName: string,
    analysisGroupId: string,
    query: string,
    pageLimit: number,
    sortField: string,
    sortOrder: string,
    filename: string,
    projectId?: string,
    taxonomyPath?: string,
    taxonomyReportQuery?: string,
    taxonomyNode?: string
  ): string {
    const args = ['token=' + encodeURIComponent(this.getToken()), 'format=xlsx'];
    if (query) {
      args.push('query=' + encodeURIComponent(query));
    }

    if (pageLimit) {
      args.push(`page-limit=${pageLimit}`);
    }

    if (sortField) {
      args.push(`sortField=${encodeURIComponent(sortField)}`);
    }

    if (sortOrder) {
      args.push(`sortOrder=${encodeURIComponent(sortOrder)}`);
    }

    if (filename) {
      args.push(`filename=${encodeURIComponent(filename)}`);
    }

    if (taxonomyPath) {
      args.push(`taxonomyPath=${encodeURIComponent(taxonomyPath.split(' ').join('%20'))}`);
    }

    if (taxonomyReportQuery) {
      args.push(`taxonomyReportQuery=${encodeURIComponent(taxonomyReportQuery)}`);
    }

    if (taxonomyNode) {
      args.push(`taxonomyReport=true`);
      args.push(`taxonomyNode=${encodeURIComponent(taxonomyNode.split(' ').join('%20'))}`);
    }

    let url = this.getBaseUrl();
    if (projectId) {
      url += `/projects/${encodeURIComponent(projectId)}`;
    }
    if (!analysisGroupId) {
      url += `/collections/${encodeURIComponent(collectionName)}`;
    }
    if (analysisName) {
      url += `/analyses/${encodeURIComponent(analysisName)}`;
    }
    if (analysisGroupId) {
      url += `/analysis-groups/${encodeURIComponent(analysisGroupId)}`;
    }
    url += '/reports/documentsOverTime/documents';

    return `${url}?${args.join('&')}`;
  }

  public async searchForDocuments(
    widgetDataSource: WidgetDataSource,
    projectId: string,
    collectionName?: string,
    query?: string,
    limit: number = 20,
    extra?: SearchOptions
  ) {
    const safeExtra = Object.assign({ sortField: 'id' }, extra || {});
    if (widgetDataSource.type === 'group') {
      safeExtra.detailed ||= widgetDataSource.groupAnalyses?.join() || '*';

      return this.searchWithinReportForGroup(
        projectId,
        widgetDataSource.groupId,
        query,
        limit,
        safeExtra
      );
    } else {
      safeExtra.detailed ||= widgetDataSource.analysisId;
      safeExtra.isSocial ||= widgetDataSource.isSocial;

      return this.searchWithinAnalysis(
        collectionName,
        widgetDataSource.analysisId,
        query,
        limit,
        safeExtra,
        projectId
      );
    }
  }

  public async searchForJourneyGroupDocuments(
    widgetDataSource: WidgetDataSource,
    projectId: string,
    query: string,
    extra: SearchOptions & {
      journeySubject: { subject: string; subjectValue: string; scoreMetric?: string };
    },
    pageSize?: number
  ): Promise<JourneyGroupDocsResult> {
    const { journeySubject, ...searchOptions } = extra ?? {};
    const paramParts: string[] = [];
    if (journeySubject) {
      paramParts.push(`subject=${journeySubject.subject}`);
      paramParts.push(`subjectValue=${journeySubject.subjectValue}`);
      if (journeySubject.scoreMetric) {
        paramParts.push(`scoreMetric=${journeySubject.scoreMetric}`);
      }
    }
    const body: Record<string, unknown> = {
      query: query || '*:*',
      'page-limit': pageSize ?? StorageAPIService.MAX_LIMIT,
      ...searchOptions,
      sortField: 'createdAt',
    };
    if (paramParts.length > 0) {
      body['params'] = paramParts.join('&');
    }
    const result = await this.post<JourneyGroupDocsResult>(
      urlFor(
        `/analysis-groups/${encodeURIComponent(widgetDataSource.groupId)}/reports/journeyGroupDocs/documents`,
        projectId
      ),
      body
    );
    return result;
  }

  public async searchWithinCollection(
    collectionName: string,
    query?: string,
    limit: number = 20,
    extra?: SearchOptions,
    projectId?: string
  ): Promise<DocumentFacetListResult> {
    //If extra.detailed exists that means we really want to search within the analaysis
    if (extra.detailed) {
      return await this.searchWithinAnalysis(
        collectionName,
        extra.detailed,
        query,
        limit,
        extra,
        projectId
      );
    }
    const options = Object.assign(
      {
        query: query || '*:*',
        'page-limit': limit,
      },
      extra || {}
    );

    return (await this.getPage<void>(
      urlFor(`/collections/${encodeURIComponent(collectionName)}/documents`, projectId),
      options as any
    )) as any as DocumentFacetListResult;
  }

  public async searchWithinAnalysis(
    collectionName: string,
    analysisName: string,
    query?: string,
    limit: number = 20,
    extra?: SearchOptions,
    projectId?: string
  ): Promise<DocumentFacetListResult> {
    const options = Object.assign(
      {
        query: query || '*:*',
        'page-limit': limit,
      },
      extra || {}
    );

    let url = '';

    if (extra?.isSocial) {
      url = `/social/projects/${projectId}/collections/${encodeURIComponent(
        collectionName
      )}/analyses/${encodeURIComponent(analysisName)}/documents`;
    } else {
      url = urlFor(
        `/collections/${encodeURIComponent(collectionName)}/analyses/${encodeURIComponent(
          analysisName
        )}/documents`,
        projectId
      );
    }

    const result = await this.postPage<void>(url, options as any);

    return result as any as DocumentFacetListResult;
  }

  public async searchWithinReportForGroup(
    projectId: string,
    groupId: string,
    query?: string,
    limit: number = 20,
    extra?: SearchOptions
  ): Promise<DocumentFacetListResult> {
    const options = Object.assign(
      {
        query: query || '*:*',
        'page-limit': limit,
      },
      extra || {}
    );

    return (await this.postPage<void>(
      urlFor(
        `/analysis-groups/${encodeURIComponent(groupId)}/reports/featureSentimentScore/documents`,
        projectId
      ),
      options as any
    )) as any as DocumentFacetListResult;
  }

  public async getFacetsWithinCollection(
    collectionName: string,
    query?: string,
    count: number = 50,
    sentimentScale: 3 | 5 = 3,
    extra?: SearchOptions
  ): Promise<FacetList> {
    const result = await this.searchWithinCollection(
      collectionName,
      query,
      1,
      Object.assign({ facets: count }, extra)
    );

    const sentimentScaleSuffix = `.sentimentPolarity${sentimentScale}`;
    const facets = result.facets;

    for (const key of Object.getOwnPropertyNames(facets)) {
      const value = facets[key];

      if (key.endsWith(sentimentScaleSuffix)) {
        const sentimentScalePrefix = key.slice(0, -19);
        const newKey = `${sentimentScalePrefix}.sentimentPolarity`;
        facets[newKey] = value;
      }
    }

    return facets;
  }

  public async getFacetsWithinAnalysis(
    collectionName: string,
    analysisName: string,
    query?: string,
    count: number = 50,
    sentimentScale: 3 | 5 = 3,
    extra?: SearchOptions,
    projectId?: string
  ): Promise<FacetList> {
    const result = await this.searchWithinAnalysis(
      collectionName,
      analysisName,
      query,
      1,
      Object.assign({ facets: count }, extra),
      projectId
    );

    const sentimentScaleSuffix = `.sentimentPolarity${sentimentScale}`;
    const facets = result.facets;

    for (const key of Object.getOwnPropertyNames(facets)) {
      const value = facets[key];

      if (key.endsWith(sentimentScaleSuffix)) {
        const sentimentScalePrefix = key.slice(0, -19);
        const newKey = `${sentimentScalePrefix}.sentimentPolarity`;
        facets[newKey] = value;
      }
    }

    return facets;
  }

  public async getFacetsWithinGroup(
    projectId: string,
    dataSource: WidgetDataSource,
    query?: string,
    count: number = 50,
    sentimentScale: 3 | 5 = 3,
    extra?: SearchOptions
  ): Promise<FacetList> {
    const result = await this.searchWithinReportForGroup(
      projectId,
      dataSource.groupId,
      query,
      1,
      Object.assign({ facets: count }, extra)
    );

    const sentimentScaleSuffix = `.sentimentPolarity${sentimentScale}`;
    const facets = result.facets;

    for (const key of Object.getOwnPropertyNames(facets)) {
      const value = facets[key];

      if (key.endsWith(sentimentScaleSuffix)) {
        const sentimentScalePrefix = key.slice(0, -19);
        const newKey = `${sentimentScalePrefix}.sentimentPolarity`;
        facets[newKey] = value;
      }
    }

    return facets;
  }

  public async getById(
    dataSourceType: { type: 'collection' | 'group'; id: string },
    documentId: string,
    analysisNames?: string[],
    projectId?: string,
    isSocial?: boolean
  ): Promise<Document> {
    const dataSourceTypeStr =
      dataSourceType.type === 'collection' ? 'collections' : 'analysis-groups';
    const options: { detailed; annotations? } = { detailed: '*' };

    if (analysisNames) {
      options.detailed = analysisNames.join(',');
      options.annotations = true;
    }

    return await this.get<Document>(
      isSocial
        ? `/social/projects/${projectId}/${dataSourceTypeStr}/${dataSourceType.id}/documents/${documentId}`
        : urlFor(
            `/${dataSourceTypeStr}/${encodeURIComponent(
              dataSourceType.id
            )}/documents/${encodeURIComponent(documentId)}`,
            projectId
          ),
      options
    );
  }

  generateSmartSummary(
    collectionName: string,
    documentId: string,
    projectId?: string,
    analysisName?: string
  ) {
    return this.get<DocumentSmartSummary>(
      urlFor(
        `/collections/${encodeURIComponent(collectionName)}/documents/${encodeURIComponent(
          documentId
        )}/summary?analysisName=${analysisName}`,
        projectId
      )
    );
  }

  public async tagDocumentsByQuery(
    collectionName: string,
    query: string,
    tags: string[],
    remove = false,
    projectId?: string
  ): Promise<TagDocumentsResponse> {
    return await this.patch<DocumentPatchResponse>(
      urlFor(`/collections/${encodeURIComponent(collectionName)}/documents/_tag`, projectId),
      {
        query,
        tags,
        remove,
      }
    );
  }

  public async tagDocumentsByQueryAg(
    analysisGroupId: string,
    query: string,
    tags: string[],
    remove = false,
    projectId?: string
  ): Promise<TagDocumentsResponse> {
    return await this.patch<DocumentPatchResponse>(
      urlFor(`/analysis-groups/${encodeURIComponent(analysisGroupId)}/documents/_tag`, projectId),
      {
        query,
        tags,
        remove,
      }
    );
  }

  public async tagDocumentsByDocIds(
    collectionName: string,
    docIds: string[],
    tags: string[],
    remove = false,
    projectId?: string
  ): Promise<TagDocumentsResponse> {
    return await this.patch<DocumentPatchResponse>(
      urlFor(`/collections/${encodeURIComponent(collectionName)}/documents/_tag`, projectId),
      {
        docIds,
        tags,
        remove,
      }
    );
  }

  public async patchDocument(
    collectionName: string,
    documentId: string,
    data: any,
    version: number,
    creator: string,
    message: string,
    projectId?: string
  ): Promise<DocumentPatchResponse> {
    return await this.patch<DocumentPatchResponse>(
      urlFor(
        `/collections/${encodeURIComponent(collectionName)}/documents/${encodeURIComponent(
          documentId
        )}`,
        projectId
      ),
      {
        data,
        version,
        creator,
        message,
      }
    );
  }

  public async getAudioUrl(
    projectId: string,
    collectionName: string,
    documentId: string,
    audioPath: string
  ) {
    if (!audioPath) {
      return '';
    }
    if (audioPath.startsWith('https://') || audioPath.startsWith('http://')) {
      return audioPath;
    }
    try {
      const response = await this.get<MediaUrlResponse>(
        urlFor(
          `/collections/${encodeURIComponent(collectionName)}/documents/${encodeURIComponent(
            documentId
          )}/media`,
          projectId
        )
      );
      return response.url;
    } catch (e) {
      return '';
    }
  }

  private getBaseAudioUrl() {
    const appConfig = (window as any).appConfig || {};
    if (appConfig.blender && appConfig.blender.audioUrl) {
      return `${appConfig.blender.audioUrl}`;
    }
    return 'http://localhost:8080';
  }
}
